import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import Button from "@client/components/ui/Button";
import ButtonLoading from "@client/components/ui/ButtonLoading";
import InputField from "@client/components/ui/InputField";
import Modal from "@client/components/ui/Modal";
import customerImage from "@client/components/ui/sharedImages/customer.svg";
import customerStore from "@client/stores/CustomerStore";
import notificationStore from "@client/stores/NotificationStore";
import Style from "@client/utils/Style";
import { Customer, CustomerInput } from "@typings/Customer";
import { InputField as InputFieldType } from "@typings/InputField";

import inputFieldsSchema from "./InputFields";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const Header = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0;
`;
const CustomerImage = styled.div`
  ${Style.exactSize("96px")}
  background: url('${customerImage}') center no-repeat;
`;
const Fields = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 32px;
  justify-content: space-between;
  margin-top: -16px;
  > div {
    margin-top: 16px;
  }
`;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 48px 32px 32px 32px;
`;
type State = {
  inputFields: InputFieldType[];
  highlightedFields: string[];
};
type Props = {
  onRequestClose: () => void;
  customer?: Customer | null;
};

@observer
class CustomerModal extends React.Component<Props, State> {
  state: State = {
    inputFields: [],
    highlightedFields: [],
  };

  UNSAFE_componentWillMount() {
    let inputFields = [...inputFieldsSchema.FIELDS];
    const customer = this.props.customer;
    if (customer) {
      inputFields = inputFields.map(field => {
        const name: keyof Customer = field.name as any;
        const value = customer![name];
        if (value) {
          return { ...field, value };
        }
        return { ...field };
      });
    }
    this.setState({ inputFields });
  }

  handleFieldChange(field: InputFieldType, value: any) {
    const inputFields = this.state.inputFields.map(f =>
      f.name === field.name ? { ...f, value } : { ...f },
    );
    this.setState({ inputFields });
  }

  async handleSaveClick() {
    const messages: string[] = [];
    const highlightedFields = this.state.inputFields
      .filter(f => {
        if (f.validate) {
          const result = f.validate(f, this.state.inputFields);
          if (typeof result === "string") {
            messages.push(result);
            return true;
          }
          return !result;
        }
        if (!f.required || f.value) {
          return false;
        }
        return true;
      })
      .map(f => f.name);
    this.setState({ highlightedFields });
    if (highlightedFields.length) {
      if (messages.length) {
        notificationStore.alert(messages[0], "error");
      }
      return;
    }
    const customer: CustomerInput = inputFieldsSchema.buildData(
      this.state.inputFields,
    );
    if (this.props.customer) {
      const success = await customerStore.update(
        this.props.customer.id,
        customer,
      );
      if (success) {
        this.props.onRequestClose();
      }
    } else {
      const addedCustomer = await customerStore.add(customer);
      if (addedCustomer) {
        this.props.onRequestClose();
      }
    }
  }

  renderButtons() {
    return (
      <Buttons>
        <Button
          secondary
          large
          onClick={this.props.onRequestClose}
          disabled={customerStore.updating}
        >
          Cancel
        </Button>
        {customerStore.updating ? (
          <ButtonLoading large>Saving...</ButtonLoading>
        ) : (
          <Button
            large
            disabled={customerStore.updating}
            onClick={() => {
              this.handleSaveClick();
            }}
          >
            Save
          </Button>
        )}
      </Buttons>
    );
  }

  renderFields() {
    return (
      <Fields>
        {this.state.inputFields.map(field => {
          return (
            <InputField
              key={field.name}
              field={field}
              disabled={customerStore.updating}
              highlight={
                !!this.state.highlightedFields.find(name => field.name === name)
              }
              onChange={value => {
                this.handleFieldChange(field, value);
              }}
            />
          );
        })}
      </Fields>
    );
  }

  render() {
    return (
      <Modal
        isOpen
        title={this.props.customer ? "Edit Customer" : "New Customer"}
        onRequestClose={this.props.onRequestClose}
      >
        <Wrapper>
          <Header>
            <CustomerImage />
          </Header>
          {this.renderFields()}
          {this.renderButtons()}
        </Wrapper>
      </Modal>
    );
  }
}

export default CustomerModal;
